import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Route, RouteRegion } from '@hero/schema';
import type { RegionCity } from '@hero/api';
import { deriveTemperatureFromVehicles } from '../deriveTemperature';
import { emptyRow, makeSheetRowId } from '../rowFactory';
import { routeToSheetRow, isRowSavable } from '../mapRoute';
import {
  EMPTY_SHEET_FILTERS,
  INITIAL_SHEET_SCOPE,
  isFilterActive,
  type SheetFilters,
  type SheetRow,
  type SheetScope,
  type SheetScopeAgreement,
  type SheetScopeDirect,
  type SheetSide,
} from '../types';

/**
 * Single source of truth for the pricing sheet. Every visual piece
 * (`PricingSheetHeader`, `PricingSheetFilters`, `PricingSheetBulkBar`,
 * `PricingSheetTable`, `PricingSheetRow`, `PricingSheetEmpty`, the footer)
 * subscribes to the store via selectors instead of prop-drilling.
 *
 * The `PricingSheetPage` keeps a thin shell around the store: it owns the
 * React Query I/O (load via `useRouteListApi`; save via the create/update/
 * delete mutations) and seeds the store with server data via
 * `seedFromRoutes`. The rest is store-driven so future surfaces (a side
 * panel, a copy-to-customer dialog, an analytics summary, …) can plug into
 * the same state without re-fetching or re-deriving anything.
 *
 * Selection is stored as `string[]` (not `Set`) so it works seamlessly with
 * immer; the table converts to a `Set` once for O(1) `.has` checks.
 */

export interface PricingSheetState {
  /** "My selling rate" vs "My buying rate" — drives the `(seller, buyer)` resolution. */
  side: SheetSide;
  /**
   * Sheet-wide scope — who is this price list for? See {@link SheetScope}
   * for the three branches (`'own'` / `'direct'` / `'agreement'`).
   * Switching scope refetches the list and reseeds the rows.
   */
  scope: SheetScope;

  /** Local editable mirror of the routes from the server. */
  rows: SheetRow[];
  /**
   * Last server snapshot the rows were seeded from — kept verbatim so
   * `resetChanges()` can rebuild without going back to React Query.
   */
  routeSeed: Route[];
  /**
   * `true` once `seedFromRoutes()` has run for the current `(side, scope)`.
   * Reset to `false` whenever `setSide` / `setScope` change the active
   * query — the rows are stale by definition until the next seed lands.
   *
   * The page reads this to distinguish "rows are empty because we haven't
   * loaded yet" (→ show skeleton) from "rows are empty because the server
   * returned nothing" (→ show empty state). Without it, scope changes
   * briefly flash the empty state between data-arrival and the seed
   * effect committing.
   */
  hasSeeded: boolean;

  /** Client-side filter strip (AND-combined). */
  filters: SheetFilters;

  /** Row ids the user has checked for bulk actions. */
  selectedIds: string[];

  /**
   * Last "A customer" scope (`'direct'` **or** `'agreement'`) that was
   * committed via `setScope`. Kept around so that toggling
   * **Everyone → A customer → Everyone → A customer** restores the user's
   * previous selection — both the customer and (if they had one) the
   * agreement — without forcing them to re-pick.
   *
   * The merged "A customer" tab uses this as the single source of truth
   * for what to restore:
   *   - `null` → no remembered selection; the picker is empty.
   *   - `{ kind: 'direct', counterparty }` → restore direct(customer).
   *   - `{ kind: 'agreement', agreement, counterparty }` → restore the
   *     full agreement scope, customer included.
   *
   * Updated automatically by `setScope` whenever a `'direct'` or
   * `'agreement'` scope is committed. Never touched on `'own'` commits —
   * peeking at Everyone preserves the memory by design. Reset to `null`
   * only by `clearRememberedCustomer` (the customer picker's explicit
   * clear button).
   */
  lastCustomerScope: SheetScopeDirect | SheetScopeAgreement | null;
}

export interface PricingSheetActions {
  // -- scope -------------------------------------------------------------
  setSide: (next: SheetSide) => void;
  /**
   * Replace the active scope. Selection is cleared eagerly so the bulk bar
   * doesn't render with ids that belong to a different scope's row set.
   * The page-level `useRouteListApi` effect picks up the change on the next
   * render and reseeds the rows automatically.
   *
   * As a side effect, committing a `'direct'` or `'agreement'` scope
   * updates {@link PricingSheetState.lastCustomerScope} so the picker
   * restores the selection on a later toggle back to "A customer".
   */
  setScope: (next: SheetScope) => void;
  /**
   * Forget the remembered "A customer" scope. Call this from the customer
   * picker's clear callback so that "I cleared this on purpose" doesn't
   * silently come back on the next toggle.
   */
  clearRememberedCustomer: () => void;
  /**
   * Wipe the entire sheet back to its initial state. Used by
   * `PricingSheetPage` on unmount so that leaving the pricing tab
   * doesn't leak scope / filters / pending edits into the next mount.
   * Reload-style persistence is handled separately by the URL params
   * the page writes — the store itself is intentionally session-scoped.
   */
  reset: () => void;

  // -- server sync -------------------------------------------------------
  /**
   * Replace the local mirror with a fresh snapshot from the server. Wipes
   * pending edits (we surface this trade-off in the UI via the "Reset
   * changes" button — see `resetChanges`).
   */
  seedFromRoutes: (routes: Route[]) => void;
  /**
   * Reseed the rows from the last `routeSeed` snapshot. Used by "Reset
   * changes" — no network call required.
   */
  resetChanges: () => void;

  // -- filters -----------------------------------------------------------
  setFilters: (next: SheetFilters) => void;
  clearFilters: () => void;

  // -- row mutations -----------------------------------------------------
  /**
   * Append a fresh row at the bottom of the sheet.
   *
   * When `seedFromFilters` is `true` (used by the empty-state CTA), the new
   * row inherits origin / destination / vehicles from the currently active
   * filters so the user doesn't have to retype what they just searched for.
   * The footer "Add row" never sets this flag — that affordance is meant to
   * stay blank by default, matching spreadsheet ergonomics.
   */
  addRow: (opts?: { seedFromFilters?: boolean }) => void;
  changeRow: (id: string, patch: Partial<SheetRow>) => void;
  duplicateRow: (id: string) => void;
  deleteRow: (id: string) => void;

  // -- selection ---------------------------------------------------------
  toggleRow: (id: string, next: boolean) => void;
  toggleAllVisible: (next: boolean, visibleIds: string[]) => void;
  clearSelection: () => void;

  // -- bulk actions ------------------------------------------------------
  bulkDuplicate: () => void;
  bulkDelete: () => void;
}

export type PricingSheetStore = PricingSheetState & PricingSheetActions;

const initialState: PricingSheetState = {
  side: 'sell',
  scope: INITIAL_SHEET_SCOPE,
  rows: [],
  routeSeed: [],
  hasSeeded: false,
  filters: EMPTY_SHEET_FILTERS,
  selectedIds: [],
  lastCustomerScope: null,
};

/**
 * Stable string representation of a scope. Two scopes that produce the same
 * `listParams` and the same save payload also produce the same signature, so
 * `setScope` can early-return on the very common "same scope, fresh object
 * reference" case (e.g. a header re-render that doesn't actually change
 * anything).
 */
function scopeSignature(scope: SheetScope): string {
  switch (scope.kind) {
    case 'own':
      return 'own';
    case 'direct':
      return `direct:${scope.counterparty.id}`;
    case 'agreement':
      return `agreement:${scope.agreement.id}:${scope.counterparty.id}`;
  }
}

/** Adapt a `RegionCity` (the picker payload) into the row's `RouteRegion`. */
function regionCityToRouteRegion(region: RegionCity): RouteRegion {
  return {
    _id: region._id,
    displayName: region.displayName,
    customCode: region.customCode,
    type: 'city',
    // `boundaries` and `center` are server-resolved from `_id`; we don't
    // have them client-side and the resolver doesn't require them on the
    // create payload.
  } as unknown as RouteRegion;
}

/** Build a fresh row pre-filled from the current filter values. */
function seedRowFromFilters(filters: SheetFilters): SheetRow {
  const base = emptyRow();
  return {
    ...base,
    origin: filters.origin ? regionCityToRouteRegion(filters.origin) : null,
    destination: filters.destination
      ? regionCityToRouteRegion(filters.destination)
      : null,
    vehicles: [...filters.vehicles],
    temperatureType:
      filters.vehicles.length > 0
        ? deriveTemperatureFromVehicles(filters.vehicles)
        : base.temperatureType,
  };
}

export const usePricingSheetStore = create<PricingSheetStore>()(
  immer((set, get) => ({
    ...initialState,

    // ---------------------------------------------------------------------
    // Scope
    // ---------------------------------------------------------------------
    setSide: next =>
      set(state => {
        if (state.side === next) return;
        state.side = next;
        // Side ↔ scope changes pick a *different* server query key (the
        // page rebuilds `listParams` from `{side, scope}`), so the rows we
        // were just showing belong to the previous query and are by
        // definition stale.
        //
        // We blank the local mirror eagerly here — instead of waiting for
        // React Query to deliver new data and a downstream `useEffect` to
        // `seedFromRoutes()` — so the page never paints a single frame of
        // the *previous* side's rows under the new side's header. The
        // page's "show skeleton while loading and rows are empty" check
        // then reliably triggers, matching how the rest of the app's
        // entity lists behave when their filters change.
        state.rows = [];
        state.routeSeed = [];
        state.hasSeeded = false;
        state.selectedIds = [];
      }),
    setScope: next =>
      set(state => {
        // Cheap stable-identity shortcut: same kind + same nested ids ⇒ noop.
        // Done as a string compare so each branch stays self-contained.
        const sig = scopeSignature(next);
        if (sig === scopeSignature(state.scope)) return;
        state.scope = next;
        // Remember the last "A customer" scope so the picker can restore
        // the user's previous customer + agreement when they toggle back
        // to the "A customer" tab. We intentionally don't touch the
        // remembered slot on a `'own'` commit — that's the whole point:
        // toggling to Everyone shouldn't forget who you were just viewing.
        if (next.kind === 'direct' || next.kind === 'agreement') {
          state.lastCustomerScope = next;
        }
        // Same reasoning as `setSide`: scope drives the server query key
        // (`isOwnRoute` / `sellerId` / `buyerId` / `agreementId`), so the
        // existing `rows` are bound to the previous scope and must be
        // dropped immediately. The next render gets a clean slate; React
        // Query refetches and `seedFromRoutes()` populates the rows that
        // actually belong to the new scope.
        state.rows = [];
        state.routeSeed = [];
        state.hasSeeded = false;
        state.selectedIds = [];
      }),
    clearRememberedCustomer: () =>
      set(state => {
        state.lastCustomerScope = null;
      }),
    reset: () =>
      set(state => {
        Object.assign(state, initialState);
      }),

    // ---------------------------------------------------------------------
    // Server sync
    // ---------------------------------------------------------------------
    seedFromRoutes: routes =>
      set(state => {
        state.routeSeed = routes;
        state.rows = routes.map(routeToSheetRow);
        state.hasSeeded = true;
        state.selectedIds = [];
      }),
    resetChanges: () =>
      set(state => {
        state.rows = state.routeSeed.map(routeToSheetRow);
        state.selectedIds = [];
      }),

    // ---------------------------------------------------------------------
    // Filters
    // ---------------------------------------------------------------------
    setFilters: next =>
      set(state => {
        state.filters = next;
      }),
    clearFilters: () =>
      set(state => {
        state.filters = EMPTY_SHEET_FILTERS;
      }),

    // ---------------------------------------------------------------------
    // Row mutations
    // ---------------------------------------------------------------------
    addRow: opts =>
      set(state => {
        const useFilterSeed =
          opts?.seedFromFilters === true && isFilterActive(state.filters);
        state.rows.push(useFilterSeed ? seedRowFromFilters(state.filters) : emptyRow());
      }),
    changeRow: (id, patch) =>
      set(state => {
        const idx = state.rows.findIndex(r => r.id === id);
        if (idx < 0) return;
        const current = state.rows[idx];
        // Status is derived in the store, never sent in the patch — that way
        // a `new` row can't accidentally be downgraded to `modified` (which
        // would push it onto the update fan-out where it'd blow up because
        // `routeId` is still null).
        const { status: _ignoredStatus, ...fieldPatch } = patch;
        const nextStatus: SheetRow['status'] =
          current.status === 'new'
            ? 'new'
            : current.status === 'deleted'
              ? 'deleted'
              : 'modified';
        state.rows[idx] = {
          ...current,
          ...fieldPatch,
          status: nextStatus,
        } as SheetRow;
      }),
    duplicateRow: id =>
      set(state => {
        const idx = state.rows.findIndex(r => r.id === id);
        if (idx < 0) return;
        const cloned: SheetRow = {
          ...state.rows[idx],
          id: makeSheetRowId(),
          routeId: null,
          status: 'new',
        };
        state.rows.splice(idx + 1, 0, cloned);
      }),
    deleteRow: id =>
      set(state => {
        const idx = state.rows.findIndex(r => r.id === id);
        if (idx < 0) return;
        const row = state.rows[idx];
        // New unsaved rows just disappear; persisted rows get tombstoned
        // so Save all can issue a real delete.
        if (row.routeId === null) {
          state.rows.splice(idx, 1);
        } else {
          state.rows[idx] = { ...row, status: 'deleted' };
        }
        state.selectedIds = state.selectedIds.filter(sid => sid !== id);
      }),

    // ---------------------------------------------------------------------
    // Selection
    // ---------------------------------------------------------------------
    toggleRow: (id, next) =>
      set(state => {
        const has = state.selectedIds.includes(id);
        if (next && !has) state.selectedIds.push(id);
        else if (!next && has)
          state.selectedIds = state.selectedIds.filter(sid => sid !== id);
      }),
    toggleAllVisible: (next, visibleIds) =>
      set(state => {
        if (!next) {
          // Drop only the visible ids, not the entire set — that way
          // selections made under a different filter aren't lost.
          state.selectedIds = state.selectedIds.filter(
            sid => !visibleIds.includes(sid),
          );
          return;
        }
        const merged = new Set(state.selectedIds);
        for (const sid of visibleIds) merged.add(sid);
        state.selectedIds = Array.from(merged);
      }),
    clearSelection: () =>
      set(state => {
        state.selectedIds = [];
      }),

    // ---------------------------------------------------------------------
    // Bulk
    // ---------------------------------------------------------------------
    bulkDuplicate: () =>
      set(state => {
        const selected = new Set(state.selectedIds);
        const toClone = state.rows.filter(r => selected.has(r.id));
        const cloned: SheetRow[] = toClone.map(r => ({
          ...r,
          id: makeSheetRowId(),
          routeId: null,
          status: 'new',
        }));
        state.rows.push(...cloned);
      }),
    bulkDelete: () =>
      set(state => {
        const selected = new Set(state.selectedIds);
        const next: SheetRow[] = [];
        for (const r of state.rows) {
          if (!selected.has(r.id)) {
            next.push(r);
            continue;
          }
          if (r.routeId === null) continue; // drop unsaved rows entirely
          next.push({ ...r, status: 'deleted' });
        }
        state.rows = next;
        state.selectedIds = [];
      }),
  })),
);

// =============================================================================
// Pure selectors — keep derived data colocated so every consumer is consistent.
// =============================================================================

/**
 * Rows the sheet should render given the current filters. Rows with pending
 * edits (`status !== 'unchanged'`) always pass so a user never loses sight
 * of an in-flight change just because it doesn't match a filter yet.
 */
export function selectVisibleRows(state: PricingSheetState): SheetRow[] {
  const { rows, filters } = state;
  if (!isFilterActive(filters)) return rows;
  return rows.filter(r => {
    if (r.status !== 'unchanged') return true;
    if (filters.origin && String(r.origin?._id) !== String(filters.origin._id))
      return false;
    if (
      filters.destination &&
      String(r.destination?._id) !== String(filters.destination._id)
    )
      return false;
    if (filters.vehicles.length > 0) {
      const overlap = r.vehicles.some(v => filters.vehicles.includes(v));
      if (!overlap) return false;
    }
    return true;
  });
}

/** Rows currently in a dirty state — drives the "pending" badge + Save all. */
export function selectPendingRows(state: PricingSheetState): SheetRow[] {
  return state.rows.filter(r => r.status !== 'unchanged');
}

/** Count of pending rows that the server will actually accept. */
export function selectSavableCount(state: PricingSheetState): number {
  return selectPendingRows(state).filter(
    r => r.status === 'deleted' || isRowSavable(r),
  ).length;
}
